import { ReviewerRole, ReviewType } from '../types'
import { EventId } from '../machineTypes'
import { AddApproverButton } from '../AddReviewers/AddApproverButton'
import { AddViewerButton } from '../AddReviewers/AddViewerButton'
import { useReviewsWizard } from '../ReviewsWizardProvider'
import { useSelector } from '@xstate/react'
import Styles from './ReviewersSelection.module.scss'
import { AddReviewerButton } from '../AddReviewers/AddReviewerButton'
import { ReviewersFields } from '../AddReviewers/ReviewersFields'
import { Value as RecipientsSelectorValue } from 'components/ShareSheet/RecipientSelector/Types'
import { ScheduleField } from './ScheduleField'
import { MessageField } from './MessageField'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { Modal } from 'almanac-ui/Modal'
import { useRef } from 'react'
import { ProseInputHandle } from 'common/ProseInput/Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useHistory } from 'react-router-dom'

interface Props {
  newEmails: string[]
  isDisabled?: boolean
}

const Title = ({ reviewType }: { reviewType: ReviewType | null }) => (
  <div className={Styles.title}>
    {reviewType === ReviewType.FEEDBACK && <>Get Informal Feedback</>}
    {reviewType === ReviewType.APPROVAL && <>Request Formal Approval</>}
    {reviewType === ReviewType.READ_RECEIPTS && <>Read Receipts</>}
  </div>
)

export const ReviewersSelection = observer(({ newEmails, isDisabled = false }: Props) => {
  const { docStore } = useRootStore()
  const { service, isLoading } = useReviewsWizard()
  const reviewers = useSelector(service, (state) => state.context.reviewers)
  const approvers = useSelector(service, (state) => state.context.approvers)
  const viewers = useSelector(service, (state) => state.context.viewers)
  const reviewType = useSelector(service, (state) => state.context.reviewType)
  const reviewerRoles = useSelector(service, (state) => state.context.reviewRoles)
  const canNavigateToTypeSelection = useSelector(service, (state) => !state.context.disableTypeSelection)
  const requiredRecipients = [...viewers, ...reviewers, ...approvers]
  const canAddReviewers = reviewType !== ReviewType.READ_RECEIPTS || !docStore?.isVersionControlled
  const messageInputRef = useRef<ProseInputHandle>(null)
  const { isMobile } = useResponsiveDesign()
  const history = useHistory()

  const handleSubmit = () => {
    if (requiredRecipients.length < 1) {
      return
    }
    service.send(EventId.CONTINUE, { newEmails })
  }

  const getValidationMessage = () => {
    if (requiredRecipients.length > 0) return 'You must write a review name'
    return 'You must select viewers, reviewers, and/or approvers first'
  }

  const onReviewersChange = (value: RecipientsSelectorValue[]) => {
    service.send(EventId.SELECT_REVIEWERS, { reviewers: value })
  }

  const onApproversChange = (value: RecipientsSelectorValue[]) => {
    service.send(EventId.SELECT_APPROVERS, { approvers: value })
  }

  const onViewersChange = (value: RecipientsSelectorValue[]) => {
    service.send(EventId.SELECT_VIEWERS, { viewers: value })
  }

  const topContent = (
    <div className={Styles.top}>
      <div className={Styles.topActions}>
        {canNavigateToTypeSelection && <Modal.BackButton onPress={() => service.send(EventId.BACK)} />}
      </div>
      <div className={Styles.topActions}>
        {reviewerRoles.length < 3 && (
          <>
            {!reviewerRoles.includes(ReviewerRole.REVIEWER) && canAddReviewers && (
              <AddReviewerButton
                onPress={() => service.send(EventId.ADD_REVIEWER_ROLE, { role: ReviewerRole.REVIEWER })}
              />
            )}
            {!reviewerRoles.includes(ReviewerRole.APPROVER) && canAddReviewers && (
              <AddApproverButton
                onPress={() => service.send(EventId.ADD_REVIEWER_ROLE, { role: ReviewerRole.APPROVER })}
              />
            )}
            {!reviewerRoles.includes(ReviewerRole.VIEWER) && (
              <AddViewerButton onPress={() => service.send(EventId.ADD_REVIEWER_ROLE, { role: ReviewerRole.VIEWER })} />
            )}
          </>
        )}
      </div>
    </div>
  )

  return (
    <>
      <Modal.Header
        title={<Title reviewType={reviewType} />}
        onClose={() => {
          history.goBack()
        }}
        actions={<>{!isMobile && topContent}</>}
      />
      <Modal.Content>
        {isMobile && topContent}
        <div className={Styles.container}>
          <ReviewersFields
            reviewers={reviewers}
            onReviewersChange={onReviewersChange}
            approvers={approvers}
            onApproversChange={onApproversChange}
            viewers={viewers}
            onViewersChange={onViewersChange}
            roles={reviewerRoles}
            disabled={isLoading || isDisabled}
            onRecipientActionClick={undefined}
          />

          <ScheduleField />
          <MessageField isDisabled={isDisabled} ref={messageInputRef} />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          {canNavigateToTypeSelection && (
            <Modal.FooterButton
              variant="tertiary"
              size="lg"
              onPress={() => service.send(EventId.BACK)}
              isDisabled={isLoading || isDisabled}
            >
              Back
            </Modal.FooterButton>
          )}
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Tooltip placement="top" label={getValidationMessage()} hide={requiredRecipients.length > 0}>
            <Modal.FooterButton
              onPress={handleSubmit}
              data-heap-id="submit-feedback-request"
              title="Send Request Primary"
              isDisabled={isLoading || isDisabled}
              size="lg"
            >
              Send Request
            </Modal.FooterButton>
          </Tooltip>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
})
